import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { TenantApi } from "@/openapi/generated";
import type { ResultOfCreatePayJunctionKeyResponse } from "@/openapi/generated/models";
import type { GetPayJunctionResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const usePayJunctionKeyCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError, data } = useMutation({
    mutationFn: async (tenantId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(TenantApi, accessToken);

      const result: ResultOfCreatePayJunctionKeyResponse =
        await service.apiAccountTenantIdPayjunctionKeyPost({
          tenantId,
          xTenantID: tenantId,
        });

      return { tenantId, result };
    },
    onSuccess: ({ tenantId, result }) => {
      const newKey = result?.data?.key;

      if (newKey) {
        queryClient.setQueryData<GetPayJunctionResponse | null>(
          [QueryKeys.PayJunctionOptions, tenantId],
          (oldData) => ({
            userName: oldData?.userName ?? null,
            enabled: oldData?.enabled ?? false,
            publishableKey: newKey,
          }),
        );
      }

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PayJunctionOptions, tenantId],
      });

      toast({
        title: "Publishable key created",
        description:
          "PayJunction publishable key has been generated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create PayJunction publishable key";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, mutateAsync, isPending, isError, isSuccess, data };
};
